import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  PlugZap,
  Save,
  ShieldCheck,
  ShieldOff,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { LockBanner } from '@/components/auth/LockBanner';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import {
  useDeleteSystemSecret,
  useSetSystemSecret,
  useSystemSecretsList,
  useTestSystemSecret,
  type SystemSecretRow,
  type TestSecretResult,
} from '@/hooks/useSystemSecrets';

/**
 * /settings/secrets (Sprint-3-Carry-Over, WT-307).
 *
 * Tabellen-View aller Systeme die einen PVE-API-Token brauchen
 * (service_type proxmox-ve|proxmox-bs ODER role=hypervisor). Pro Zeile:
 *  - "Token gesetzt"-Badge, Action zum Setzen / Aktualisieren / Löschen
 *  - Modal mit Show/Hide-Toggle, optionaler Description, Verbindungstest.
 *
 * Backend muss entsperrt sein - das Master-Passwort wird beim Login an den
 * Argon2id-KEK-Cache übergeben, nur dann ist secrets.encrypt(...) verfügbar.
 */

export const Route = createFileRoute('/settings/secrets')({
  component: SecretsPage,
});

function SecretsPage(): JSX.Element {
  const list = useSystemSecretsList();
  const sorted = useMemo(() => {
    const rows = list.data?.data ?? [];
    return [...rows].sort(
      (a, b) =>
        a.customer_name.localeCompare(b.customer_name) ||
        a.system_hostname.localeCompare(b.system_hostname)
    );
  }, [list.data]);

  const [editTarget, setEditTarget] = useState<SystemSecretRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SystemSecretRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const columns = useMemo((): ReadonlyArray<Column<SystemSecretRow>> => {
    return [
      {
        id: 'kunde',
        header: 'Kunde',
        filterValue: (row) => row.customer_name,
        cell: (row) => <span className="font-medium">{row.customer_name}</span>,
      },
      {
        id: 'system',
        header: 'System',
        filterValue: (row) => [row.system_hostname, row.service_type ?? row.role].join(' '),
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-mono text-xs">{row.system_hostname}</span>
            <span className="text-xs text-muted">{row.service_type ?? row.role}</span>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        filter: 'select',
        filterValue: (row) => (row.has_token ? 'Token gesetzt' : 'Kein Token'),
        cell: (row) =>
          row.has_token ? (
            <Badge tone="success">
              <ShieldCheck className="mr-1 inline size-3" aria-hidden="true" />
              Token gesetzt
            </Badge>
          ) : (
            <Badge tone="warning">
              <ShieldOff className="mr-1 inline size-3" aria-hidden="true" />
              Kein Token
            </Badge>
          ),
      },
      {
        id: 'aktionen',
        header: 'Aktionen',
        filter: false,
        headerClassName: 'text-right',
        className: 'text-right',
        cell: (row) => (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<KeyRound className="size-3" />}
              onClick={() => setEditTarget(row)}
              data-testid={`secret-edit-${row.system_id}`}
            >
              {row.has_token ? 'Aktualisieren' : 'Setzen'}
            </Button>
            {row.has_token && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Trash2 className="size-3" />}
                onClick={() => setDeleteTarget(row)}
                data-testid={`secret-delete-${row.system_id}`}
              >
                Entfernen
              </Button>
            )}
          </div>
        ),
      },
    ];
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">PVE-API-Tokens</h1>
        </div>
        <p className="text-muted">
          Tokens zum Aufruf der Proxmox-API (Version-Pinning, Backup-Status).
        </p>
        <div
          role="note"
          className="mt-2 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 p-3 text-sm"
        >
          <Lock className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          <p>
            <strong>Diese Tokens werden mit dem Master-Passwort verschlüsselt</strong>{' '}
            (Argon2id-derived Key). Das Backend muss entsperrt sein - d.h. ein User
            war eingeloggt und der KEK-Cache ist warm. Andernfalls liefert der
            Server <code>423 Locked</code>.
          </p>
        </div>
      </header>

      <LockBanner />


      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md border border-success/30 bg-success/5 p-3 text-sm text-success"
        >
          {toast}
        </div>
      )}

      <Card
        title="Systeme mit PVE-API-Bedarf"
        description={
          <>
            Auswahl: <code>service_type IN (proxmox-ve, proxmox-bs)</code> oder{' '}
            <code>role = hypervisor</code>.
          </>
        }
      >
        {list.isLoading && <SkeletonRows rows={4} cols={4} />}
        {list.isError && <ErrorAlert error={list.error} />}
        {!list.isLoading && !list.isError && sorted.length === 0 && (
          <EmptyState
            icon={<KeyRound className="size-8" />}
            title="Keine PVE-Systeme gefunden"
            description="Lege im Inventar einen Hypervisor mit role=hypervisor an oder ordne einem System einen proxmox-ve/proxmox-bs Service-Type zu."
          />
        )}
        {sorted.length > 0 && (
          <DataTable
            caption="PVE-API-Token pro System"
            columns={columns}
            rows={sorted}
            rowKey={(row) => row.system_id}
            storageKey="pve-token"
          />
        )}
      </Card>

      <SecretEditModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={(name) => {
          setEditTarget(null);
          setToast(`Token für ${name} gespeichert.`);
          window.setTimeout(() => setToast(null), 4_000);
        }}
      />

      <SecretDeleteDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={(name) => {
          setDeleteTarget(null);
          setToast(`Token für ${name} entfernt.`);
          window.setTimeout(() => setToast(null), 4_000);
        }}
      />
    </div>
  );
}

interface EditProps {
  target: SystemSecretRow | null;
  onClose: () => void;
  onSaved: (systemHostname: string) => void;
}

function SecretEditModal({ target, onClose, onSaved }: EditProps): JSX.Element {
  const [token, setToken] = useState('');
  const [description, setDescription] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [testResult, setTestResult] = useState<TestSecretResult | null>(null);

  const setSecret = useSetSystemSecret();
  const testSecret = useTestSystemSecret();

  // Reset state on target change.
  useEffect(() => {
    setToken('');
    setDescription('');
    setShowToken(false);
    setTestResult(null);
    setSecret.reset();
    testSecret.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.system_id]);

  if (!target) {
    return (
      <Dialog open={false} onClose={onClose} title="">
        <span />
      </Dialog>
    );
  }

  const onTest = (): void => {
    setTestResult(null);
    testSecret.mutate(
      token ? { systemId: target.system_id, token } : { systemId: target.system_id },
      {
        onSuccess: (res) => setTestResult(res),
      }
    );
  };

  const onSave = (): void => {
    if (!token) return;
    setSecret.mutate(
      {
        systemId: target.system_id,
        token,
        ...(description ? { description } : {}),
      },
      {
        onSuccess: () => onSaved(target.system_hostname),
      }
    );
  };

  return (
    <Dialog
      open={Boolean(target)}
      onClose={onClose}
      title={`PVE-Token: ${target.system_hostname}`}
      description={`Kunde: ${target.customer_name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="secondary"
            leftIcon={<PlugZap className="size-4" />}
            onClick={onTest}
            loading={testSecret.isPending}
            disabled={setSecret.isPending}
            data-testid="secret-test-btn"
          >
            Verbindung testen
          </Button>
          <Button
            leftIcon={<Save className="size-4" />}
            onClick={onSave}
            loading={setSecret.isPending}
            disabled={!token || testSecret.isPending}
            data-testid="secret-save-btn"
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="PVE API Token"
              type={showToken ? 'text' : 'password'}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="PVEAPIToken=user@pam!tokenid=xxxx-xxxx-..."
              autoComplete="off"
              data-testid="secret-token-input"
              hint={
                target.has_token
                  ? 'Aktualisiert den existierenden Token. Alter Token wird sofort verworfen.'
                  : 'Format: user@realm!tokenid=secret'
              }
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => setShowToken((s) => !s)}
            leftIcon={
              showToken ? <EyeOff className="size-4" /> : <Eye className="size-4" />
            }
            aria-label={showToken ? 'Token verbergen' : 'Token anzeigen'}
          >
            {showToken ? 'Verbergen' : 'Anzeigen'}
          </Button>
        </div>

        <Input
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="z.B. monitoring-readonly, gültig bis 2027"
          hint="Wird im Audit-Log neben dem Token-Setzen gespeichert."
        />

        {testResult && (
          <div
            role="status"
            aria-live="polite"
            className={[
              'rounded-md border p-3 text-sm',
              testResult.ok
                ? 'border-success/30 bg-success/5 text-success'
                : 'border-danger/30 bg-danger/5 text-danger',
            ].join(' ')}
            data-testid="secret-test-result"
          >
            {testResult.ok ? (
              <>
                <p className="font-medium">Verbindung OK</p>
                <p className="mt-1 text-xs">
                  PVE-Version: <code>{testResult.pve_version ?? 'unbekannt'}</code>
                  {testResult.latency_ms != null && (
                    <> · Latenz: {testResult.latency_ms} ms</>
                  )}
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">Verbindung fehlgeschlagen</p>
                {testResult.error && <p className="mt-1 text-xs">{testResult.error}</p>}
              </>
            )}
          </div>
        )}

        {testSecret.isError && <ErrorAlert error={testSecret.error} />}
        {setSecret.isError && <ErrorAlert error={setSecret.error} />}
      </div>
    </Dialog>
  );
}

interface DeleteProps {
  target: SystemSecretRow | null;
  onClose: () => void;
  onDeleted: (systemHostname: string) => void;
}

function SecretDeleteDialog({
  target,
  onClose,
  onDeleted,
}: DeleteProps): JSX.Element {
  const del = useDeleteSystemSecret();

  useEffect(() => {
    del.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.system_id]);

  if (!target) {
    return (
      <Dialog open={false} onClose={onClose} title="">
        <span />
      </Dialog>
    );
  }

  const onConfirm = (): void => {
    del.mutate(target.system_id, {
      onSuccess: () => onDeleted(target.system_hostname),
    });
  };

  return (
    <Dialog
      open={Boolean(target)}
      onClose={onClose}
      title="Token wirklich entfernen?"
      description={`${target.customer_name} – ${target.system_hostname}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            leftIcon={<Trash2 className="size-4" />}
            onClick={onConfirm}
            loading={del.isPending}
            data-testid="secret-delete-confirm"
          >
            Entfernen
          </Button>
        </>
      }
    >
      <p className="inline-flex items-start gap-2 text-sm text-muted">
        <AlertTriangle className="mt-0.5 size-4 text-warning" aria-hidden="true" />
        <span>
          Aktionen die diesen PVE-Token brauchen (Version-Pinning, Backup-Status)
          schlagen sofort fehl. Der Token wird im Audit-Log als gelöscht
          dokumentiert.
        </span>
      </p>
      {del.isError && <ErrorAlert error={del.error} className="mt-3" />}
    </Dialog>
  );
}
